import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import {
  personApi,
  type CustomFieldDefinition,
} from '@/modules/person/services/person.api';
import { BrainScopeRail, type ScopeSelection, type ScopeWorkspace } from './BrainScopeRail';

// ─── Schema constants ───────────────────────────────────────────────────────

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select (single)' },
  { value: 'multiselect', label: 'Select (multi)' },
  { value: 'boolean', label: 'True / false' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
] as const;

const FIELD_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  FIELD_TYPES.map((t) => [t.value, t.label]),
);

function fieldScopeOf(field: { workspaceId?: string | null }): 'workspace' | 'org' {
  return field.workspaceId ? 'workspace' : 'org';
}

const SCOPE_QUERY = 'scope';
const WS_QUERY = 'ws';

function readScope(params: URLSearchParams): ScopeSelection | null {
  const ws = params.get(WS_QUERY);
  if (ws) return { kind: 'workspace', workspaceId: ws };
  const scope = params.get(SCOPE_QUERY);
  if (scope === 'org') return { kind: 'org' };
  return null;
}

function writeScope(params: URLSearchParams, scope: ScopeSelection): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(SCOPE_QUERY);
  next.delete(WS_QUERY);
  if (scope.kind === 'org') next.set(SCOPE_QUERY, 'org');
  else if (scope.kind === 'workspace') next.set(WS_QUERY, scope.workspaceId);
  return next;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function CustomFieldsBrainPage() {
  const { hasPermission, currentWorkspaceId, workspaces } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const canManage = hasPermission('person.manage_custom_fields');
  const canView = hasPermission('person.view') || canManage;

  // Custom fields use the legacy person.* permission set rather than the
  // brain.* split, so for now we treat workspace+org symmetrically — anyone
  // with manage_custom_fields can edit at either scope.
  const accessibleWorkspaces: ScopeWorkspace[] = useMemo(() => {
    return workspaces.map((ws) => ({
      _id: ws._id,
      name: ws.name,
      current: ws._id === currentWorkspaceId,
    }));
  }, [workspaces, currentWorkspaceId]);

  const defaultScope: ScopeSelection = useMemo(() => {
    const current = accessibleWorkspaces.find((w) => w.current);
    if (current) return { kind: 'workspace', workspaceId: current._id };
    if (accessibleWorkspaces[0]) return { kind: 'workspace', workspaceId: accessibleWorkspaces[0]._id };
    return { kind: 'org' };
  }, [accessibleWorkspaces]);

  const scope = readScope(searchParams) ?? defaultScope;
  const setScope = (next: ScopeSelection) => setSearchParams(writeScope(searchParams, next));

  const [fields, setFields] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState<CustomFieldDefinition | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await personApi.listCustomFields();
      setFields(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load custom fields';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) void refresh();
    else setLoading(false);
  }, [canView, refresh]);

  const visibleItems = useMemo(() => {
    return fields.filter((f) => {
      const s = fieldScopeOf(f);
      if (scope.kind === 'org') return s === 'org';
      if (scope.kind === 'workspace') return f.workspaceId === scope.workspaceId;
      return false;
    });
  }, [fields, scope]);

  const scopeLabel = (() => {
    if (scope.kind === 'org') return 'Organization';
    if (scope.kind === 'workspace') {
      return accessibleWorkspaces.find((w) => w._id === scope.workspaceId)?.name || 'Workspace';
    }
    return '';
  })();

  const handleCreate = async (data: FormData) => {
    try {
      const { data: created } = await personApi.createCustomField({
        key: data.key,
        label: data.label,
        type: data.type,
        options: data.options,
        description: data.description,
        isRequired: data.isRequired,
        isSearchable: false,
        syncToPerson: data.syncToPerson,
        workspaceId: scope.kind === 'workspace' ? scope.workspaceId : null,
      });
      setFields((prev) => [...prev, created]);
      setEditorOpen(false);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Failed to create custom field');
      setError(message);
    }
  };

  const handleUpdate = async (existing: CustomFieldDefinition, data: FormData) => {
    try {
      const { data: updated } = await personApi.updateCustomField(existing._id, {
        label: data.label,
        description: data.description,
        options: data.options,
        isRequired: data.isRequired,
      });
      setFields((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
      setEditorOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update custom field';
      setError(message);
    }
  };

  const handleDelete = async (field: CustomFieldDefinition) => {
    if (!confirm(`Delete custom field "${field.label}"? Existing data using this field is not removed.`)) return;
    const backup = fields;
    setFields((prev) => prev.filter((f) => f._id !== field._id));
    try {
      await personApi.deleteCustomField(field._id);
    } catch (err: unknown) {
      setFields(backup);
      const message = err instanceof Error ? err.message : 'Failed to delete custom field';
      setError(message);
    }
  };

  const handleToggleSyncToPerson = async (field: CustomFieldDefinition) => {
    const next = !field.syncToPerson;
    setFields((prev) => prev.map((f) => (f._id === field._id ? { ...f, syncToPerson: next } : f)));
    setBusyIds((s) => new Set(s).add(field._id));
    try {
      await personApi.updateCustomField(field._id, { syncToPerson: next });
    } catch (err: unknown) {
      setFields((prev) => prev.map((f) => (f._id === field._id ? { ...f, syncToPerson: field.syncToPerson } : f)));
      const message = err instanceof Error ? err.message : 'Failed to toggle sync';
      setError(message);
    } finally {
      setBusyIds((s) => {
        const n = new Set(s);
        n.delete(field._id);
        return n;
      });
    }
  };

  if (!canView) {
    return (
      <div className="brain-page">
        <div className="brain-content">
          <div className="brain-empty">
            <p>You don&apos;t have permission to view custom fields.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="brain-page">
      <BrainScopeRail
        title="Custom Fields"
        subtitle="Application form schema"
        current={scope}
        showOrg
        workspaces={accessibleWorkspaces}
        onSelect={setScope}
      />

      <section className="brain-content">
        <header className="brain-content-header">
          <div>
            <h1>{scopeLabel}</h1>
            <p>
              {scope.kind === 'org'
                ? 'Fields available across every workspace in this organization.'
                : 'Fields scoped to this workspace.'}{' '}
              Toggle <span className="cf-person-pill cf-person-pill--inline">Person</span> on a field to also save its value to the candidate&apos;s person profile.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              className="brain-primary-button"
              onClick={() => {
                setEditorTarget(null);
                setEditorOpen(true);
              }}
            >
              <Plus size={14} strokeWidth={1.7} />
              <span>New field</span>
            </button>
          )}
        </header>

        {error && (
          <div className="brain-error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>dismiss</button>
          </div>
        )}

        {loading ? (
          <div className="brain-loading">Loading…</div>
        ) : visibleItems.length === 0 ? (
          <div className="brain-empty">
            <p>No custom fields in {scopeLabel} yet.</p>
            {canManage && (
              <button
                type="button"
                className="brain-primary-button"
                onClick={() => {
                  setEditorTarget(null);
                  setEditorOpen(true);
                }}
              >
                <Plus size={14} strokeWidth={1.7} />
                <span>Create the first one</span>
              </button>
            )}
          </div>
        ) : (
          <ul className="brain-flat-list">
            {visibleItems.map((f) => (
              <li key={f._id} className="brain-row brain-row-card cf-row">
                <span className={`cf-type-pill cf-type-${f.type}`}>{FIELD_TYPE_LABEL[f.type] || f.type}</span>
                <div className="brain-row-main cf-row-main">
                  <div className="brain-row-title">
                    <span>{f.label}</span>
                    {f.isRequired && <span className="cf-required-dot" title="Required" />}
                  </div>
                  <div className="cf-row-meta">
                    <code className="cf-key">{f.key}</code>
                    {f.options && f.options.length > 0 && (
                      <span className="cf-row-options">{f.options.join(' · ')}</span>
                    )}
                  </div>
                  {f.description && <p className="brain-row-description">{f.description}</p>}
                </div>
                {canManage && (
                  <div className="cf-row-side">
                    <button
                      type="button"
                      className={
                        f.syncToPerson
                          ? 'cf-person-pill cf-person-pill--active'
                          : 'cf-person-pill'
                      }
                      onClick={() => handleToggleSyncToPerson(f)}
                      disabled={busyIds.has(f._id)}
                      title={
                        f.syncToPerson
                          ? 'Syncs to person profile (click to disable)'
                          : 'Click to also save to person profile'
                      }
                    >
                      {f.syncToPerson ? '✓ Person' : 'Person'}
                    </button>
                    <div className="brain-row-actions cf-row-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setEditorTarget(f);
                          setEditorOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button type="button" className="danger" onClick={() => handleDelete(f)}>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {editorOpen && (
        <CustomFieldEditor
          existing={editorTarget}
          scopeLabel={scopeLabel}
          onClose={() => setEditorOpen(false)}
          onSubmit={(data) => {
            if (editorTarget) void handleUpdate(editorTarget, data);
            else void handleCreate(data);
          }}
        />
      )}
    </div>
  );
}

// ─── Editor ─────────────────────────────────────────────────────────────────

type FormData = {
  key: string;
  label: string;
  type: string;
  options: string[];
  description: string;
  isRequired: boolean;
  syncToPerson: boolean;
};

function CustomFieldEditor({
  existing,
  scopeLabel,
  onClose,
  onSubmit,
}: {
  existing: CustomFieldDefinition | null;
  scopeLabel: string;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
}) {
  const [label, setLabel] = useState(existing?.label || '');
  const [type, setType] = useState(existing?.type || 'text');
  const [options, setOptions] = useState<string[]>(existing?.options || []);
  const [optionDraft, setOptionDraft] = useState('');
  const [description, setDescription] = useState(existing?.description || '');
  const [isRequired, setIsRequired] = useState(existing?.isRequired || false);
  const [syncToPerson, setSyncToPerson] = useState(existing?.syncToPerson || false);

  const showOptions = type === 'select' || type === 'multiselect';
  const isCreate = !existing;

  // Auto-derive key from label on create only (matches existing behavior).
  const derivedKey =
    existing?.key ||
    label
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!label.trim()) return;
    onSubmit({
      key: derivedKey,
      label: label.trim(),
      type,
      options: showOptions ? options : [],
      description: description.trim(),
      isRequired,
      syncToPerson,
    });
  };

  function addOption() {
    const v = optionDraft.trim();
    if (!v || options.includes(v)) {
      setOptionDraft('');
      return;
    }
    setOptions([...options, v]);
    setOptionDraft('');
  }

  function removeOption(value: string) {
    setOptions(options.filter((o) => o !== value));
  }

  return (
    <div className="brain-modal-backdrop">
      <form onSubmit={submit} className="brain-modal">
        <header>
          <h2>{isCreate ? 'New custom field' : `Edit field`}</h2>
          <p>Scope: {scopeLabel}</p>
        </header>

        <label className="brain-field">
          <span>Label</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            autoFocus
            placeholder="e.g. Visa status"
          />
        </label>

        {derivedKey && (
          <label className="brain-field">
            <span>Key</span>
            <code className="cf-key cf-key-readonly">{derivedKey || '—'}</code>
            {isCreate && (
              <small>Auto-generated from the label. Used as the identifier in the database.</small>
            )}
            {!isCreate && <small>Keys can&apos;t be changed once a field is created.</small>}
          </label>
        )}

        <label className="brain-field">
          <span>Type</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={!isCreate}
          >
            {FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {!isCreate && <small>Type can&apos;t be changed once a field is created.</small>}
        </label>

        {showOptions && (
          <label className="brain-field">
            <span>Options</span>
            <div className="cf-options">
              {options.map((opt) => (
                <span key={opt} className="cf-option-chip">
                  {opt}
                  <button type="button" onClick={() => removeOption(opt)} aria-label={`Remove ${opt}`}>
                    <X size={11} strokeWidth={2} />
                  </button>
                </span>
              ))}
              <input
                className="cf-option-input"
                value={optionDraft}
                onChange={(e) => setOptionDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOption();
                  }
                  if (e.key === 'Backspace' && !optionDraft && options.length > 0) {
                    setOptions(options.slice(0, -1));
                  }
                }}
                placeholder={options.length === 0 ? 'Type and press Enter…' : 'Add another…'}
              />
            </div>
          </label>
        )}

        <label className="brain-field">
          <span>Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="What this field captures, how recruiters should use it…"
          />
        </label>

        <div className="cf-toggle-row">
          <label className="brain-field-inline">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
            />
            <span>Required on the application form</span>
          </label>

          {isCreate && (
            <label className="brain-field-inline">
              <input
                type="checkbox"
                checked={syncToPerson}
                onChange={(e) => setSyncToPerson(e.target.checked)}
              />
              <span>Also save to candidate&apos;s person profile</span>
            </label>
          )}
        </div>

        <footer>
          <button type="button" className="brain-secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="brain-primary-button" disabled={!label.trim()}>
            {isCreate ? 'Create field' : 'Save changes'}
          </button>
        </footer>
      </form>
    </div>
  );
}
